import getDashedTitle from '@/utilities/getDashedTitle';
import EventBus from '@/utilities/eventBus';
import hideDropdownsMixin from '@/mixins/hideDropdownsMixin';
import isFavourite from '@/utilities/favourites/isFavourite';
import cardStatuses from '@/mixins/cardStatuses';
import { ResourceType } from '@/types/resources';
import { nanoid } from 'nanoid';

export default {
  mixins: [hideDropdownsMixin, cardStatuses],
  data() {
    return {
      draggingWatch: null,
      uniqueRowId: nanoid(6),
    };
  },
  computed: {
    isEpic() {
      return this.card.type === ResourceType.Epic;
    },
    numberOfChecked() {
      if (this.localOnlyCard || !this.visible) return 0;
      const checklists = this.card.checklists || [];
      let count = 0;
      checklists.forEach((cl) => {
        (cl.items || []).forEach((cli) => {
          if (!cli.checked) return;
          count += 1;
        });
      });
      return count;
    },
    completed() {
      return this.numberOfChecked === this.totalNumberOfChecks;
    },
    cardIsDropTarget() {
      return this.$store.getters.dropTargetId === `c-${this.card.id}`;
    },
    projectMembers() {
      if (!this.visible) return [];
      return this.$store.getters.getCurrentMembers;
    },
    cardOwner() {
      if (!this.visible) return {};
      return this.projectMembers.find((m) => m.id === this.card.user_id_posted) || {};
    },
    commentsCount() {
      return this.card.total_comments;
    },
    filesCount() {
      return this.card.total_files;
    },
    isDueDateSet() {
      return this.card.due_date;
    },
    isDueDate() {
      if (!this.visible) return false;
      return this.card.due_date * 1000 > new Date().getTime();
    },
    isDueDateLessThanFourDays() {
      if (!this.visible) return false;
      return this.card.due_date * 1000 < new Date().getTime() + 4 * this.$constants.DAY;
    },
    isCardCompleted() {
      return this.card.status === this.cardStatuses.completed.id;
    },
    dueDateClass() {
      if (!this.visible || this.isCardCompleted) return '';
      if (this.isDueDate) {
        if (this.isDueDateLessThanFourDays) {
          return 'due-soon';
        }
        return '';
      }
      return 'overdue';
    },
    isDueDateToday() {
      if (!this.visible) return false;
      const currentDate = new Date();
      const cardDate = new Date(this.card.due_date * 1000);
      return (
        currentDate.getFullYear() === cardDate.getFullYear() &&
        currentDate.getMonth() === cardDate.getMonth() &&
        currentDate.getDate() === cardDate.getDate()
      );
    },
    cardDueDate() {
      if (!this.visible) return '';
      const date = new Date(this.card.due_date * 1000);
      const day = date.getDate();
      const months = this.translate('monthsAbbreviation').split(',');
      const month = months[date.getMonth()];
      if (this.isDueDateToday) {
        return 'Today';
      }
      return `${day} ${month}`;
    },
    cardDragging() {
      if (!this.visible) return false;
      return (
        this.draggingIsOn &&
        [ResourceType.Epic, ResourceType.Card].includes(this.draggedEntity.entityName)
      );
    },
    localOnlyCard() {
      return this.card.localCardId;
    },
    teamSlug() {
      return this.$store.getters.getTeamSlug;
    },
    isCardForEdit() {
      return this.$store.getters.getCurrentCardIdActionEdit === this.card.id;
    },
    cardIdForMembersDropdown() {
      return this.$store.getters.getCurrentCardIdMemberDropdown === this.card.id;
    },
    cardList() {
      return this.$store.getters.lists[this.card.list_id] || {};
    },

    tags() {
      if (!this.visible) return [];
      return this.$store.getters.getTags;
    },

    cardTags() {
      if (!this.visible) return [];

      const cardNewTags = this.cardNew?.tags;
      if (!cardNewTags) return [];

      return cardNewTags
        .map((t) => this.tags[t.id] || t)
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name));
    },

    flatBoards() {
      if (!this.visible && !this.isChildCard) return {};
      return this.$store.getters.getFlatBoards;
    },

    draggingEnd() {
      return this.$store.getters.getDraggingEnd;
    },

    cards() {
      if (!this.visible && !this.isChildCard) return {};
      return this.$store.getters.getCards;
    },

    cardNew() {
      if (this.card.button || this.card.localCard) return this.card;

      return this.cards[this.cardId] || this.card;
    },

    getCurrentCardIdActionEdit() {
      return this.$store.getters.getCurrentCardIdActionEdit;
    },

    isViewOrMyWork() {
      return (
        this.isViewOpen ||
        [this.$constants.myWorkRouteName, this.$constants.memberRouteName].includes(
          this.$route.name
        )
      );
    },

    currentUserId() {
      return this.$store.getters.getUser.id;
    },

    isAnyCardDisplayed() {
      return this.$store.getters.getIsAnyCardDisplayed;
    },

    currentCardLayout() {
      return this.$store.getters.getLayoutOfDisplayedCard(this.card.id);
    },

    dockedCard() {
      return this.currentCardLayout === this.$constants.docked;
    },

    cardLink() {
      const dashedCardTitle = getDashedTitle(this.card.title);
      let pathName;

      if (this.isEpic) {
        pathName = this.$constants.epicRoute.path;
      } else if (this.card.sprint_id) {
        pathName = 'sprintcard';
      } else {
        pathName = 'card';
      }
      const teamSlug = this.$store.getters.getTeamSlug;
      if (!this.card.id) return undefined;
      return `/${teamSlug}/${pathName}-${this.card.id}-${dashedCardTitle}`;
    },
  },
  methods: {
    openCard(cardId) {
      if (this.cardId === cardId) {
        this.cardClick();
      }
    },
    cardClick() {
      if (this.preventOpenCard) {
        this.preventOpenCard = false;
        return;
      }
      if (this.localOnlyCard || this.draggingEnd) return;
      if (this.showEditTitle) return;
      this.hideEditMode();
      this.hideMemberDropdown();
      this.$store.dispatch('setBoardViewNoDestroy', true);
      this.setCurrentCard();

      let scrollLeft;
      let wrap;

      if (this.dockedCard) {
        // save initial scroll position to revert it after clicking on card - https://app.superthread.com/superthread/card-12389
        wrap = document.querySelector('.board-view-wrap .__panel');
        scrollLeft = wrap.scrollLeft;
      }

      this.$router.push({
        path: this.cardLink,
        query: {
          userNavigated: true,
        },
      });

      if (this.dockedCard) {
        // revert initial scroll position - https://app.superthread.com/superthread/card-12389
        this.$nextTick(() => {
          wrap.scrollLeft = scrollLeft;
        });
      }
    },
    hideEditMode() {
      if (!this.dropdownItemClicked) {
        this.showEditTitle = false;
      }
      this.dropdownItemClicked = false;
      if (this.isCardForEdit) {
        this.hideCardActionsDropdown();
      }
    },
    updateCardTitle(title) {
      this.card.title = title;
    },
    toggleActionsDropdown() {
      this.hideMemberDropdown();

      if (this.getCurrentCardIdActionEdit === this.card.id) {
        return;
      }

      EventBus.$on('renameBoardCard', this.showEditInput);
      this.$store.dispatch('setCurrentCardIdForActionEdit', this.card.id);
      this.$store.dispatch('setCurrentGroupByListIdCardActionEdit', this.listId);
      this.$store.dispatch('setCurrentCardUniqueRowId', this.uniqueRowId);
      // Add dynamic watch for card id for action edit
      const cardIdForActionEditWatch = this.$watch('getCurrentCardIdActionEdit', (newVal) => {
        if (newVal !== this.card.id) {
          EventBus.$off('renameBoardCard', this.showEditInput);
          cardIdForActionEditWatch();
        }
      });
      this.tempTitle = this.card.title;
      this.showCustomSections = true;
    },

    handleInlineRenameButton(id) {
      this.hideMemberDropdown();

      if (this.isCardForEdit) {
        this.hideCardActionsDropdown();
      }
      if (this.card.id === id) {
        this.tempTitle = this.card.title;
        this.showEditTitle = true;
      }
    },

    showEditInput({ id, listId, hideEditKeyboard }) {
      if (this.isEpic) return;
      if (this.card.id === id && (!listId || this.listId === listId)) {
        this.dropdownItemClicked = true;
        this.showEditTitle = true;
        if (hideEditKeyboard) {
          this.hideEditMode();
        }
        this.draggingWatch = this.$watch('cardDragging', (newVal) => {
          if (newVal) {
            this.hideEditMode();
          }
        });
      }
    },

    toggleCardMembersDropdownClick(e) {
      this.toggleCardMembersDropdown({
        e,
        cardId: this.card.id || this.cardNew.id,
        listId: this.listId,
      });
    },

    toggleCardMembersDropdown({
      e,
      cardId = this.card.id || this.cardNew.id,
      listId = this.listId,
    }) {
      let cardIdToSet;
      if (this.card.type === ResourceType.Epic) {
        cardIdToSet = `${ResourceType.Epic}-${cardId}`;
      } else if (this.isViewOrMyWork) {
        cardIdToSet = cardId;
      } else {
        cardIdToSet = listId != null ? `${listId}-${cardId}` : cardId;
      }

      this.hideCardActionsDropdown();
      this.$store.dispatch('setCurrentGroupByListIdCardActionEdit', listId);
      this.$store.dispatch('setCurrentCardIdMemberDropdown', cardIdToSet);
      this.$store.dispatch('setCurrentCardMembersDropdown', this.card || this.cardNew);
      this.$store.dispatch('setAddMembersTarget', {
        id: this.card.id,
        type: this.$constants.routeNames.boardCard,
        parentListId: this.card.list_id,
      });
    },
    clickOutsideHandler() {
      if (this.isFocusElement()) return;
      if (this.showEditTitle) {
        this.saveTitle();
      }
      if (this.isCardForEdit) {
        this.hideEditMode();
      }
    },
    saveTitle() {
      if (
        this.card.title !== this.tempTitle &&
        this.tempTitle !== '' &&
        this.tempTitle.trim().length
      ) {
        this.tempTitle = this.tempTitle.trim();
        this.$store.dispatch('updateCard', {
          cardId: this.card.id,
          card: { title: this.tempTitle },
          type: this.card.type,
        });
        this.$store.dispatch('localUpdateCardField', {
          fieldName: 'title',
          fieldValue: this.tempTitle,
          cardId: this.card.id,
          timeUpdated: new Date().getTime(),
          userIdUpdated: this.currentUserId,
        });
        if (isFavourite(this.card.id, 'card')) {
          this.$store.dispatch('localUpdateResourceInFavourites', {
            resource_id: this.card.id,
            resource_type: 'card',
            title: this.tempTitle,
          });
        }
      }
      this.hideEditMode();
      if (this.$store.getters.getIsCardDisplayed(this.card.id)) {
        const dashedCardTitle = getDashedTitle(this.tempTitle);
        const path = `/${this.$store.getters.getTeamSlug}/card-${this.card.id}-${dashedCardTitle}`;
        if (this.$route.path !== path) {
          history.replaceState(
            {
              ...history.state,
              cardId: this.card.id,
            },
            null,
            path
          );
        }
      }
    },
    setCurrentCard() {
      const card = {
        id: this.card.id,
        list_cards: this.listCards,
      };
      if (this.inEpicBoard) {
        const currentEpicDisplayed = this.$store.getters.getDisplayedCards.filter(
          (dc) => this.$store.getters.getCardById(dc.id).type === ResourceType.Epic
        );
        this.$store.dispatch('setDisplayedCards', [...currentEpicDisplayed, card]);
      } else {
        this.$store.dispatch('setDisplayedCards', [card]);
      }
    },
  },
};
